import React from 'react';
import PropTypes from 'prop-types';
import DateFormat from 'Components/Format/DateFormat';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { darken, lighten } from '@material-ui/core';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';

const styles = theme => ({
  root: {
    display: 'flex',
    '&:hover': {
      backgroundColor: lighten(theme.palette.facet.accentBlue, 0.7),
      cursor: 'pointer',
    },
    alignItems: 'center',
    maxHeight: '110px',
  },
  selected: {
    backgroundColor: lighten(theme.palette.facet.accentBlue, 0.7),
  },
  timeInfo: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  date: {
    display: 'flex',
    justifyContent: 'flex-end',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.facet.primaryGray,
    minWidth: '160px',
  },
  time: {
    display: 'flex',
    justifyContent: 'flex-end',
    color: theme.palette.facet.primaryGray,
  },
  eventInfo: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    borderBottom: `1px dashed ${darken(theme.palette.facet.lightGray2, 0.1)}`,
    overflow: 'hidden',
    maxWidth: '50vw',
    maxHeight: '110px',
    padding: theme.spacing(2, 3, 2, 3),
    textOverflow: 'ellipsis',
  },
  eventType: {
    fontWeight: theme.typography.fontWeightBold,
  },
  eventLabel: {
    maxHeight: '80px',
    overflow: 'hidden',
    display: 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  eventExpanded: {},
  eventExpandedContent: {},
  iconCircle: {
    position: 'relative',
    display: 'flex',
    borderRadius: '50%',
    height: '40px',
    width: '40px',
    alignSelf: 'center',
    justifySelf: 'center',
    alignContent: 'center',
    justifyContent: 'center',
    '&:before': {
      position: 'absolute',
      border: `1px dashed ${theme.palette.facet.primaryGray}`,
      width: 0,
      height: '75px',
      display: 'block',
      content: "''",
      left: '50%',
      zIndex: 0,
      top: '40px',
      marginLeft: '-1px',
    },
  },
  icon: {
    fontSize: '30px',
    alignSelf: 'center',
    justifySelf: 'center',
    color: theme.palette.common.white,
  },
  // this CSS is for handling RichTextDisplays, which add HTML elements directly to the page. the first element may not
  // necessarily be a <p> tag, so in order to limit the size of the timeline elements, we go through and hide any
  // element that isn't the first child, and apply limits to the first. ul, ol and blockquotes are special cases.
  richText: {
    overflow: 'hidden',
    display: 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconContainer: {
    paddingBottom: theme.spacing(2),
  },
  customIcon: {
    width: '20px',
  },
  customEmailIcon: {
    width: '25px',
    height: '20px',
    alignSelf: 'center',
    justifySelf: 'center',
  },
});

function TimelineEvent({
  backgroundColor,
  categoryName: cn,
  eventTime,
  icon,
  name: n,
  description,
  onClick,
  useCustomIcon,
  classes,
  className,
  selected,
  createdBy,
  category,
}) {
  const date = eventTime * 1000;

  let categoryName = cn;
  let name = n;

  if (categoryName === 'Task') {
    categoryName = name;
    name = null;
  }

  const categoryDisplayName = categoryName === 'Meeting'
  || categoryName === 'Event'
  || categoryName.startsWith('Client Escalated')
  || (categoryName.startsWith('Client Engagement') && name !== 'File Uploaded')
    ? categoryName
    : `${categoryName} created by: ${createdBy}`;

  return (
    <div
      test={category}
      className={classNames({
        [classes.root]: true,
        [className]: true,
        [classes.selected]: selected,
      })}
      role="presentation"
      onClick={() => onClick()}
    >
      <div className={classes.timeInfo}>
        <div className={classes.date}>
          <DateFormat
            date={date}
            format={{ month: 'long', day: 'numeric', year: 'numeric' }}
          />
        </div>
        <div className={classes.time}>
          <DateFormat
            date={date}
            format={{ hour12: true, hour: 'numeric', minute: '2-digit' }}
          />
        </div>
      </div>
      <div className={classes.iconContainer}>
        <div className={classes.iconCircle} style={{ backgroundColor }}>
          {useCustomIcon
            ? (
              <img
                src={icon}
                className={classNames({
                  [classes.customIcon]: true,
                  [classes.customEmailIcon]: categoryName === 'Email',
                })}
              />
            )
            : <FontAwesome
              icon={icon}
              className={classes.icon}
            />
          }
        </div>
      </div>
      <div className={classes.eventInfo}>
        <div className={classes.eventType}>{categoryDisplayName}</div>
        {categoryName === 'Note' || categoryName === 'Agenda' ? (
          <RichTextDisplay content={name} className={classes.richText} />
        ) : (
          <div className={classes.eventLabel}>
            <div>{name}</div>
            <RichTextDisplay content={description} className={classes.richText} />
          </div>
        )}
      </div>
    </div>
  );
}

TimelineEvent.propTypes = {
  backgroundColor: PropTypes.string,
  categoryName: PropTypes.string.isRequired,
  eventTime: PropTypes.string.isRequired,
  icon: PropTypes.string,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  useCustomIcon: PropTypes.bool,
  selected: PropTypes.bool,
  createdBy: PropTypes.string,
  category: PropTypes.string,
};

TimelineEvent.defaultProps = {
  backgroundColor: '',
  icon: '',
  onClick: null,
  className: '',
  useCustomIcon: false,
  selected: false,
  createdBy: '',
  category: '',
};

export default withStyles(styles)(TimelineEvent);
